import React, { Suspense, lazy, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X, Bookmark, Share2, Box, MapPin, User, Phone, ChevronLeft, Eye } from 'lucide-react';
import { shortViews } from './listingFormat.jsx';
import LoopVideo, { filmSrc } from './loopVideo.jsx';
import { isSaved, toggleSaved, subscribeSaved, isAccountSynced } from './savedStore.jsx';
import { prepareShare } from './shareDesign.jsx';
import ShareSheet from './shareSheet.jsx';
import LivePicker from './livePicker.jsx';
import ListingPage from './listingPage.jsx';
import { markViewed, getDesign } from './designsApi.jsx';
import { openProfile, phoneText, telHref, pushBackLayer } from './navBus.jsx';
import './reelViewer.scss';

// «ورود» به خانه: موتورِ سه‌بعدی سنگین است — فقط وقتی کاربر واقعاً «ورود» را زد بارگذاری می‌شود
const DesignWalk = lazy(() => import('./designWalk.jsx'));

// نمای تمام‌صفحه‌ی طرح‌ها مثلِ ریلز: فیلمِ عمودی، با کشیدنِ انگشت بالا/پایین طرحِ بعدی/قبلی.
// items: طرح‌ها (همان شکلِ پاسخِ سرور)، startIndex، onClose، onEndReached (بارگذاریِ ادامه)
// history: دکمه‌ی «برگشت»ِ گوشی هم نما را می‌بندد

const fa = (n) => Number(n).toLocaleString('fa-IR');
const DEAL = { sale: 'فروش', rent: 'رهن و اجاره' };
const PROPERTY = { apartment: 'آپارتمان', villa: 'ویلایی', land: 'زمین', office: 'اداری', shop: 'مغازه' };
const viewed = new Set(); // در همین نوبتِ باز بودنِ برنامه

function priceText(l) {
  if (!l) return '';
  if (l.dealType === 'rent') {
    const parts = [];
    if (l.deposit) parts.push(`ودیعه ${fa(l.deposit)}`);
    if (l.rent) parts.push(`اجاره ${fa(l.rent)}`);
    return parts.length ? `${parts.join(' · ')} تومان` : '';
  }
  return l.price ? `${fa(l.price)} تومان` : '';
}

// near: این ریل یا ریل‌های کنارش فعال است → ویدیو ساخته شود. preload: ریلِ بعدی از قبل دانلود می‌شود تا کشیدن
// به بعدی بدونِ انتظار باشد — ولی فقط بعد از این‌که ویدیوی فعلی آماده‌ی پخشِ بی‌وقفه شد (onReady)، تا دانلودِ
// بعدی پهنای باندِ ویدیوی در حالِ پخش را نگیرد
function ReelItem({ design, active, near, preload = 'metadata', onReady, onToast, onEnter, onOwner, onDetails }) {
  const l = design.listing || {};
  const [saved, setSaved] = useState(() => isSaved(design.id));
  useEffect(() => subscribeSaved(() => setSaved(isSaved(design.id))), [design.id]);

  // کدِ لینکِ اشتراک از قبل گرفته شود (آیفون «اشتراک» را فقط بلافاصله بعد از لمس اجازه می‌دهد)
  useEffect(() => {
    if (active) prepareShare(design);
  }, [active, design]);

  // بازدید: وقتی ۱٫۵ ثانیه روی این طرح ماند
  useEffect(() => {
    if (!active || viewed.has(design.id)) return undefined;
    const t = setTimeout(() => {
      viewed.add(design.id);
      markViewed(design.id);
    }, 1500);
    return () => clearTimeout(t);
  }, [active, design.id]);

  const meta = [
    l.area && `${fa(l.area)} متر`,
    l.rooms && `${fa(l.rooms)} خواب`,
    l.floor != null && l.floor !== '' && `طبقه ${fa(l.floor)}${l.totalFloors ? ` از ${fa(l.totalFloors)}` : ''}`,
    l.buildYear && `${fa(l.buildYear)} سال ساخت`,
  ].filter(Boolean);
  const owner = design.owner;

  const handleSave = () => {
    const now = toggleSaved(design);
    setSaved(now);
    onToast(now ? (isAccountSynced() ? 'ذخیره شد' : 'ذخیره شد — روی همین گوشی') : 'از ذخیره‌ها برداشته شد');
  };
  // منوی اشتراکِ خودمان — پیامک گزینه‌ی اول
  const [shareOpen, setShareOpen] = useState(false);
  const handleShare = () => setShareOpen(true);
  const handleShareResult = (r) => {
    if (r === 'copied') onToast('لینک کپی شد — در واتس‌اپ، تلگرام یا پیامک بفرستید');
    else if (r === 'failed') onToast('اشتراک‌گذاری ممکن نشد');
  };

  return (
    <section className="reel__item">
      {shareOpen && <ShareSheet design={design} onClose={() => setShareOpen(false)} onResult={handleShareResult} />}
      {near && design.filmUrl ? (
        <LoopVideo
          className="reel__video"
          src={filmSrc(design)}
          poster={design.thumbUrl}
          active={active}
          preload={active ? 'auto' : preload}
          onReady={active ? onReady : undefined}
        />
      ) : (
        design.thumbUrl && <img className="reel__video" src={design.thumbUrl} alt="" />
      )}
      <div className="reel__shade" />

      <div className="reel__actions">
        {/* تعدادِ بازدید (فقط نمایش — لایک و کامنت نداریم) */}
        <span className="reel__views" aria-label={`${design.views || 0} بازدید`}>
          <Eye size={25} strokeWidth={1.8} />
          <span>{shortViews(design.views)}</span>
        </span>
        <button type="button" onClick={handleSave} className={saved ? 'is-on' : ''} aria-label="ذخیره">
          <Bookmark size={26} strokeWidth={1.8} fill={saved ? 'currentColor' : 'none'} />
          <span>{saved ? 'ذخیره شد' : 'ذخیره'}</span>
        </button>
        <button type="button" onClick={handleShare} aria-label="اشتراک‌گذاری">
          <Share2 size={25} strokeWidth={1.8} />
          <span>اشتراک</span>
        </button>
        {/* آگهیِ بدونِ پلانِ سه‌بعدی (modelUrl از سرور null) دکمه‌ی ورود ندارد */}
        {design.modelUrl !== null && (
          <button type="button" onClick={() => onEnter(design)} aria-label="ورود به خانه">
            <Box size={25} strokeWidth={1.8} />
            <span>ورود</span>
          </button>
        )}
        {owner && owner.phone && (
          <a href={telHref(owner.phone)} aria-label="تماس با مشاور">
            <Phone size={24} strokeWidth={1.8} />
            <span>تماس</span>
          </a>
        )}
      </div>

      <div className="reel__info">
        {owner && (
          <button type="button" className="reel__owner" onClick={() => onOwner(owner)}>
            <span className="reel__avatar">{owner.firstName ? owner.firstName.charAt(0) : <User size={14} />}</span>
            <strong>
              {owner.firstName} {owner.lastName}
            </strong>
            {owner.role === 'consultant' && <em>مشاور املاک</em>}
            <ChevronLeft size={16} strokeWidth={2} />
          </button>
        )}
        {owner && owner.phone && (
          <a className="reel__phone" href={telHref(owner.phone)}>
            <Phone size={14} strokeWidth={2} />
            <span dir="ltr">{phoneText(owner.phone)}</span>
          </a>
        )}
        {/* طراحیِ کاربرِ عادی («خانه‌ی رویایی من») — آگهیِ فروش/اجاره نیست */}
        {!design.listing && owner && owner.role !== 'consultant' && <span className="reel__chip">طراحیِ کاربر · خانه‌ی رویایی</span>}
        {(DEAL[l.dealType] || PROPERTY[l.propertyType]) && (
          <span className="reel__chip">{[DEAL[l.dealType], PROPERTY[l.propertyType]].filter(Boolean).join(' · ')}</span>
        )}
        {l.title && (
          <h2 onClick={() => onDetails(design)} role="button">
            {l.title}
          </h2>
        )}
        {design.listing && (
          <button type="button" className="reel__details" onClick={() => onDetails(design)}>
            جزئیاتِ آگهی ‹
          </button>
        )}
        {priceText(l) && <p className="reel__price">{priceText(l)}</p>}
        {meta.length > 0 && <p className="reel__meta">{meta.join(' • ')}</p>}
        {l.province && (
          <p className="reel__meta">
            <MapPin size={13} strokeWidth={2} /> {l.province}
          </p>
        )}
      </div>
    </section>
  );
}

// onOwner(owner): لمسِ نامِ مشاور (پیش‌فرض: پروفایلش از طریقِ App). layer: z-index (ریلزِ داخلِ پروفایلِ مشاور)
function ReelViewer({ items, startIndex = 0, onClose, onEndReached, history = true, onOwner, layer }) {
  const boxRef = useRef(null);
  const [active, setActive] = useState(startIndex);
  const [readyIdx, setReadyIdx] = useState(-1); // ریلی که ویدیویش آماده‌ی پخشِ بی‌وقفه است
  const [toast, setToast] = useState(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // شروع از همان طرحی که لمس شد
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (el) el.scrollTop = startIndex * el.clientHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // کدام طرح الان روی صفحه است
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      const i = Math.round(el.scrollTop / Math.max(1, el.clientHeight));
      setActive((prev) => (prev === i ? prev : i));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (onEndReached && active >= items.length - 3) onEndReached();
  }, [active, items.length, onEndReached]);

  // اسکرولِ صفحه‌ی زیرین قفل؛ دکمه‌ی برگشتِ گوشی = بستن
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const release = history ? pushBackLayer(() => closeRef.current && closeRef.current()) : null;
    return () => {
      document.body.style.overflow = prev;
      if (release) release();
    };
  }, [history]);

  const showToast = (text) => setToast({ text, key: Date.now() });

  // «ورود» به خانه — طرح‌های ذخیره‌شده‌ی قدیمی آدرسِ فایل‌های سه‌بعدی را ندارند: اول از سرور گرفته می‌شود
  // طرحی که از لینکِ اشتراک (shareCode) باز شده: «ورود» = بازدیدِ مشترک (اول انتخابِ اسم و آدمک)
  const [walk, setWalk] = useState(null); // { design, live? }
  const [picker, setPicker] = useState(null); // design
  const [details, setDetails] = useState(null); // صفحه‌ی کاملِ آگهی
  const handleEnter = async (design) => {
    let d = design;
    if (!(d.modelUrl && d.lightmapUrl && d.planUrl)) {
      try {
        const r = await getDesign(design.id);
        if (!r || !r.design || !r.design.modelUrl) {
          showToast('این طرح نمایشِ سه‌بعدی ندارد');
          return;
        }
        d = { ...r.design, shareCode: design.shareCode };
      } catch (e) {
        showToast(e.message);
        return;
      }
    }
    if (d.shareCode) setPicker(d);
    else setWalk({ design: d });
  };
  const handleOwner = (owner) => {
    if (onOwner) onOwner(owner);
    else openProfile(owner);
  };
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  return (
    <div className="reel" style={layer ? { zIndex: layer } : undefined}>
      <div className="reel__scroller" ref={boxRef}>
        {items.map((d, i) => (
          <ReelItem
            key={d.id}
            design={d}
            active={i === active && !walk && !picker && !details}
            near={Math.abs(i - active) <= 1}
            preload={i === active + 1 && readyIdx === active ? 'auto' : 'metadata'}
            onReady={() => setReadyIdx(i)}
            onToast={showToast}
            onEnter={handleEnter}
            onOwner={handleOwner}
            onDetails={setDetails}
          />
        ))}
      </div>
      <button type="button" className="reel__close" onClick={onClose} aria-label="بستن">
        <X size={22} strokeWidth={2} />
      </button>
      {toast && (
        <div key={toast.key} className="reel__toast">
          {toast.text}
        </div>
      )}
      {walk && (
        <Suspense
          fallback={
            <div className="reel__walk-loading">
              <p>در حالِ آماده کردنِ خانه…</p>
            </div>
          }
        >
          <DesignWalk design={walk.design} live={walk.live} onClose={() => setWalk(null)} />
        </Suspense>
      )}
      {details && <ListingPage design={details} onClose={() => setDetails(null)} />}
      {picker && (
        <LivePicker
          code={picker.shareCode}
          design={picker}
          onClose={() => setPicker(null)}
          onStart={(session, name) => {
            setWalk({ design: picker, live: { session, name } });
            setPicker(null);
          }}
        />
      )}
    </div>
  );
}

export default ReelViewer;
